<template>
  <div :class="$style.twoColumnLayout">
    <!-- Left Column: Lifestyle DNA -->
    <div :class="$style.lifestyleDNA">
      <h3 :class="$style.columnTitle">
        {{ t('neighbourhood.insights.lifestyleDNA') }}
      </h3>
      <div :class="$style.dnaTags">
        <div v-for="tag in lifestyleDNA" :key="tag.name" :class="$style.dnaTag">
          <div :class="$style.iconWrapper">
            <NeighbourhoodIcon :name="tag.icon" size="large" />
          </div>
          <div :class="$style.tagText">
            <div :class="$style.tagName">{{ tag.name }}</div>
            <div v-if="tag.description" :class="$style.tagDescription">
              {{ tag.description }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column: Key Places -->
    <div :class="$style.keyPlaces">
      <h3 :class="$style.columnTitle">
        {{ t('neighbourhood.insights.keyPlacesFor') }}
        <span :class="$style.personaHighlight">{{ personaLabel }}</span>
      </h3>
      <div :class="$style.placesList">
        <div v-for="place in keyPlaces" :key="place.name" :class="$style.placeItem">
          <div :class="$style.iconWrapper">
            <NeighbourhoodIcon :name="place.icon" size="large" />
          </div>
          <div :class="$style.placeInfo">
            <div :class="$style.placeName">{{ place.name }}</div>
            <div :class="$style.placeDistance">
              {{ place.distance }}
            </div>
          </div>
        </div>
      </div>

      <button type="button" :class="$style.exploreLink" @click="handleExplore">
        <span>{{ t('neighbourhood.insights.exploreNearby') }}</span>
        <NeighbourhoodIcon name="map-pin" size="medium" :class="$style.exploreIcon" />
      </button>

      <ExploreNearbyModal
        :isModalVisible="isExploreNearbyModalVisible"
        @close="handleCloseExploreNearbyModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

import { ref } from 'vue';
import ExploreNearbyModal from './ExploreNearbyModal.vue';
import NeighbourhoodIcon from './NeighbourhoodIcon.vue';

const { t } = useI18n();

const emit = defineEmits<{
  explore: [];
}>();

const isExploreNearbyModalVisible = ref(false);

const handleOpenExploreNearbyModal = () => {
  isExploreNearbyModalVisible.value = true;
};

const handleCloseExploreNearbyModal = () => {
  isExploreNearbyModalVisible.value = false;
};

const handleExplore = () => {
  emit('explore');
  handleOpenExploreNearbyModal();
};

export interface LifestyleDNATag {
  icon: string;
  name: string;
  description?: string;
}

export interface KeyPlace {
  icon: string;
  name: string;
  distance: string;
  walkable?: boolean;
  type?: string;
  description?: string;
}

defineProps<{
  lifestyleDNA: LifestyleDNATag[];
  keyPlaces: KeyPlace[];
  personaLabel: string;
}>();
</script>

<style module lang="scss">
@import '@/assets/variables';

.twoColumnLayout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @container (max-width: #{$neighbourhood-single-col-max}) {
    grid-template-columns: 1fr;
  }
}

.lifestyleDNA,
.keyPlaces {
  padding: 24px;
  border-radius: 12px;
  background: #ffffff;
  border: none;
}

.columnTitle {
  font-size: 16px;
  font-weight: 400;
  color: #666666;
  margin: 0 0 16px 0;
  line-height: 24px;
}

.personaHighlight {
  color: #00b6b6;
  font-weight: 800;
}

// Lifestyle DNA Styles
.dnaTags {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.dnaTag {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 800;
  color: #333333;
}

.tagName {
  line-height: 1.4;
}

.tagText {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tagDescription {
  font-size: 14px;
  font-weight: 400;
  color: #595959;
  line-height: 21px;
}

// Key Places Styles
.placesList {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 16px;
}

.placeItem {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.placeInfo {
  flex: 1;
  min-width: 0; // Allow text to wrap
}

.placeName {
  font-size: 16px;
  font-weight: 800;
  color: #333333;
  margin-bottom: 0;
  line-height: 1.3;
}

.placeDistance {
  font-size: 14px;
  color: #595959;
  line-height: 21px;
}

.exploreLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  color: #2266e8;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  margin-bottom: 0;
  padding-top: 8px;
  border-top: 1px solid #c8c8c8;
  width: 100%;
}

.exploreLink:hover {
  text-decoration: underline;
}

.exploreIcon {
  color: #2266e8;
  width: 12px;
  height: 12px;
}

.iconWrapper {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
}

.iconWrapper :global(svg) {
  color: #333333;
  width: 20px;
  height: 20px;
  display: block;
}
</style>
